import React, { useCallback, useEffect, useRef, useState } from 'react'
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button, LinearProgress,
  Table, TableBody, TableHead, TableRow, TableCell, Checkbox, IconButton,
  Typography, Grid, Tooltip, Tabs, Tab
} from '@mui/material'
import { ClassNameMap, withStyles } from '@mui/styles'
import Draggable from 'react-draggable'
import { toast } from 'react-toastify'
import DeleteOutlinedIcon from '@mui/icons-material/DeleteOutlined'
import CloudUploadOutlinedIcon from '@mui/icons-material/CloudUploadOutlined'
import GetAppIcon from '@mui/icons-material/GetApp'
import SyncIcon from '@mui/icons-material/Sync'
import stylesheet from '../detail/detail-container.stylesheet'
import Helper from '../../../utils/helper'
import { PatientService } from '../../../services/patient.service'
import DialogTitleHeaderComponent from '../detail/common/dialog-title-header.component'
import ConfirmationDialogComponent from '../detail/common/confirmation-dialog.component'
import moment from 'moment'

interface UltromicsFile {
  name: string
  size: number
  modified: number
}

interface UltromicsReport {
  name: string
  size: number
  created: number
  type?: 'import' | 'sync'
}

interface UltromicsEpicRawReport {
  name: string
  mrn: string
  import_type: string
  size: number
  created: number
}

interface IProps {
  openUltromicsImport: boolean
  onClose: () => void
  classes: ClassNameMap
}

const UltromicsImportDialogComponent: React.FC<IProps> = ({ openUltromicsImport, onClose, classes }) => {
  const patientService = useRef(new PatientService()).current
  const dialogRef = useRef(null) as any
  const mountedRef = useRef(true)

  const [activeTab, setActiveTab] = useState(0)
  const [files, setFiles] = useState<UltromicsFile[]>([])
  const [selectedFiles, setSelectedFiles] = useState<string[]>([])
  const [loading, setLoading] = useState(false)
  const [importing, setImporting] = useState(false)
  const [killPending, setKillPending] = useState(false)
  const [canForceStop, setCanForceStop] = useState(false)
  const [confirmStop, setConfirmStop] = useState(false)
  const [importProgress, setImportProgress] = useState<any>(null)

  const [confirmDelete, setConfirmDelete] = useState<string | null>(null)
  const [uploading, setUploading] = useState(false)
  const [reports, setReports] = useState<UltromicsReport[]>([])
  const [loadingReports, setLoadingReports] = useState(false)
  const [processedFiles, setProcessedFiles] = useState<UltromicsFile[]>([])
  const [loadingProcessed, setLoadingProcessed] = useState(false)
  const [confirmDeleteProcessed, setConfirmDeleteProcessed] = useState<string | null>(null)
  const [pendingDeleteReport, setPendingDeleteReport] = useState<{ name: string; type: 'import' | 'sync' } | null>(null)
  const [measurementReports, setMeasurementReports] = useState<UltromicsReport[]>([])
  const [loadingMeasurementReports, setLoadingMeasurementReports] = useState(false)
  const [pendingDeleteMeasurementReport, setPendingDeleteMeasurementReport] = useState<string | null>(null)
  const [epicRawReports, setEpicRawReports] = useState<UltromicsEpicRawReport[]>([])
  const [loadingEpicRawReports, setLoadingEpicRawReports] = useState(false)
  const [pendingDeleteEpicRawReport, setPendingDeleteEpicRawReport] = useState<string | null>(null)
  const [syncing, setSyncing] = useState(false)
  const [azureSyncEnabled, setAzureSyncEnabled] = useState(false)

  const fileInputRef = useRef<HTMLInputElement>(null)
  const pollIntervalRef = useRef<any>(null)
  const pollStartRef = useRef<number>(0)
  const prevRowsRef = useRef<number>(-1)
  const MAX_POLL_DURATION_MS = 15 * 60 * 1000 // 15 min without progress (> one 600s patient budget)
  const FORCE_STOP_AFTER_MS = 60 * 1000
  const syncPollRef = useRef<any>(null)
  const syncPollCountRef = useRef(0)

  const fetchFiles = useCallback(() => {
    setLoading(true)
    patientService.listUltromicsFiles().then((res: any) => {
      if (!mountedRef.current) return
      setFiles(res.data?.files || [])
      setLoading(false)
    }).catch((err: any) => {
      if (!mountedRef.current) return
      toast.error(Helper.getErrorText(err), Helper.bottom_center())
      setLoading(false)
    })
  }, [patientService])

  const fetchReports = useCallback(() => {
    setLoadingReports(true)
    let importFailed = false
    let syncFailed = false
    Promise.all([
      patientService.listUltromicsReports().catch(() => { importFailed = true; return { data: { reports: [] } } }),
      patientService.listUltromicsSyncReports().catch(() => { syncFailed = true; return { data: { reports: [] } } }),
    ]).then(([importRes, syncRes]) => {
      if (!mountedRef.current) return
      if (importFailed && syncFailed) {
        toast.error('Failed to load reports', Helper.bottom_center())
      }
      const importReports = (importRes.data?.reports || []).map((r: UltromicsReport) => ({ ...r, type: 'import' as const }))
      const syncReports = (syncRes.data?.reports || []).map((r: UltromicsReport) => ({ ...r, type: 'sync' as const }))
      const all = [...importReports, ...syncReports]
      all.sort((a: UltromicsReport, b: UltromicsReport) => b.created - a.created)
      setReports(all)
      setLoadingReports(false)
    }).catch(() => {
      if (mountedRef.current) setLoadingReports(false)
    })
  }, [patientService])

  const fetchMeasurementReports = useCallback(() => {
    setLoadingMeasurementReports(true)
    patientService.listUltromicsMeasurementReports().then((res: any) => {
      if (!mountedRef.current) return
      setMeasurementReports(res.data?.reports || [])
      setLoadingMeasurementReports(false)
    }).catch((err: any) => {
      if (!mountedRef.current) return
      toast.error(Helper.getErrorText(err), Helper.bottom_center())
      setLoadingMeasurementReports(false)
    })
  }, [patientService])

  const fetchEpicRawReports = useCallback(() => {
    setLoadingEpicRawReports(true)
    patientService.listUltromicsEpicRawReports().then((res: any) => {
      if (!mountedRef.current) return
      setEpicRawReports(res.data?.reports || [])
      setLoadingEpicRawReports(false)
    }).catch((err: any) => {
      if (!mountedRef.current) return
      toast.error(Helper.getErrorText(err), Helper.bottom_center())
      setLoadingEpicRawReports(false)
    })
  }, [patientService])

  const fetchProcessedFiles = useCallback(() => {
    setLoadingProcessed(true)
    patientService.listUltromicsProcessedFiles().then((res: any) => {
      if (!mountedRef.current) return
      setProcessedFiles(res.data?.files || [])
      setLoadingProcessed(false)
    }).catch((err: any) => {
      if (!mountedRef.current) return
      toast.error(Helper.getErrorText(err), Helper.bottom_center())
      setLoadingProcessed(false)
    })
  }, [patientService])

  useEffect(() => {
    if (!openUltromicsImport) return
    mountedRef.current = true
    if (pollIntervalRef.current) { clearInterval(pollIntervalRef.current); pollIntervalRef.current = null }
    if (syncPollRef.current) { clearInterval(syncPollRef.current); syncPollRef.current = null }
    setSyncing(false)
    setImporting(false)
    setUploading(false)
    setSelectedFiles([])
    setLoading(false)
    setLoadingReports(false)
    setLoadingProcessed(false)
    setLoadingMeasurementReports(false)
    setFiles([])
    setReports([])
    setProcessedFiles([])
    setMeasurementReports([])
    setPendingDeleteMeasurementReport(null)
    setEpicRawReports([])
    setLoadingEpicRawReports(false)
    setPendingDeleteEpicRawReport(null)
    setAzureSyncEnabled(false)
    setConfirmDelete(null)
    setConfirmDeleteProcessed(null)
    setPendingDeleteReport(null)
    setKillPending(false)
    setCanForceStop(false)
    setConfirmStop(false)
    setImportProgress(null)
    fetchFiles()
    patientService.getUltromicsAzureSyncConfig().then((res: any) => {
      if (mountedRef.current) setAzureSyncEnabled(res.data?.configured || false)
    }).catch(() => {})
    patientService.checkUltromicsImportStatus().then((res: any) => {
      if (!mountedRef.current) return
      const status = res.data?.status
      if (status === 'ongoing' || status === 'kill_pending') {
        setImporting(true)
        setKillPending(status === 'kill_pending')
        const killedAt = res.data?.kill_requested_at
        setCanForceStop(!!killedAt && Date.now() - killedAt * 1000 > FORCE_STOP_AFTER_MS)
        setImportProgress(res.data?.progress || null)
        toast.info('An import batch is already running.', Helper.bottom_center())
        startPolling()
      }
    }).catch(() => { /* best-effort on open */ })
    return () => {
      mountedRef.current = false
      if (pollIntervalRef.current) clearInterval(pollIntervalRef.current)
      if (syncPollRef.current) clearInterval(syncPollRef.current)
    }
  }, [fetchFiles, openUltromicsImport, patientService])

  const handleTabChange = (_: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue)
    if (newValue === 1) {
      fetchReports()
    } else if (newValue === 2) {
      fetchProcessedFiles()
    } else if (newValue === 3) {
      fetchMeasurementReports()
    } else if (newValue === 4) {
      fetchEpicRawReports()
    }
  }

  const handleSelectAll = (checked: boolean) => {
    setSelectedFiles(checked ? files.map(f => f.name) : [])
  }

  const handleSelectFile = (filename: string, checked: boolean) => {
    setSelectedFiles(prev =>
      checked ? [...prev, filename] : prev.filter(f => f !== filename)
    )
  }

  const handleDeleteFile = (filename: string) => {
    setConfirmDelete(filename)
  }

  const confirmDeleteFile = (confirmed: boolean) => {
    if (confirmed && confirmDelete) {
      patientService.deleteUltromicsFile(confirmDelete).then(() => {
        if (!mountedRef.current) return
        toast.success('File deleted', Helper.bottom_center())
        setSelectedFiles(prev => prev.filter(f => f !== confirmDelete))
        setConfirmDelete(null)
        fetchFiles()
      }).catch((err: any) => {
        if (!mountedRef.current) return
        toast.error(Helper.getErrorText(err), Helper.bottom_center())
        setConfirmDelete(null)
      })
    } else {
      setConfirmDelete(null)
    }
  }

  const handleStartImport = () => {
    if (selectedFiles.length === 0) return
    setImporting(true)
    patientService.startUltromicsImport(selectedFiles).then(() => {
      if (!mountedRef.current) return
      toast.success('Import started', Helper.bottom_center())
      startPolling()
    }).catch((err: any) => {
      if (!mountedRef.current) return
      toast.error(Helper.getErrorText(err), Helper.bottom_center())
      setImporting(false)
    })
  }

  const handleStopImport = () => setConfirmStop(true)

  const confirmStopImport = (confirmed: boolean) => {
    setConfirmStop(false)
    if (!confirmed) return
    patientService.killUltromicsImport(canForceStop).then((res: any) => {
      if (!mountedRef.current) return
      setKillPending(true)
      const forced = res.data?.message === 'force_released'
      toast.info(forced ? 'Import force-stopped.' : 'Stop requested — waiting for the import to halt…',
        Helper.bottom_center())
    }).catch((err: any) => {
      if (!mountedRef.current) return
      toast.error(Helper.getErrorText(err), Helper.bottom_center())
    })
  }

  const startPolling = () => {
    if (pollIntervalRef.current) return
    pollStartRef.current = Date.now()
    prevRowsRef.current = -1
    pollIntervalRef.current = setInterval(() => {
      // Timeout safety valve
      if (Date.now() - pollStartRef.current > MAX_POLL_DURATION_MS) {
        clearInterval(pollIntervalRef.current)
        pollIntervalRef.current = null
        if (mountedRef.current) {
          setImporting(false)
          toast.warn('Import status check timed out. Please check the Status tab for results.', Helper.bottom_center())
          fetchReports()
          fetchEpicRawReports()
          setActiveTab(1)
        }
        return
      }

      patientService.checkUltromicsImportStatus().then((res: any) => {
        if (!mountedRef.current) {
          clearInterval(pollIntervalRef.current)
          pollIntervalRef.current = null
          return
        }
        const status = res.data?.status
        if (status === 'ongoing' || status === 'kill_pending') {
          setImportProgress(res.data?.progress || null)
          setKillPending(status === 'kill_pending')
          const killedAt = res.data?.kill_requested_at
          setCanForceStop(!!killedAt && Date.now() - killedAt * 1000 > FORCE_STOP_AFTER_MS)
          const rows = res.data?.progress?.rows_processed
          if (typeof rows === 'number' && rows > prevRowsRef.current) {
            prevRowsRef.current = rows
            pollStartRef.current = Date.now()   // progress genuinely advanced → reset stall timer
          }
        } else if (status === 'completed') {
          clearInterval(pollIntervalRef.current)
          pollIntervalRef.current = null
          setImporting(false)
          setKillPending(false)
          setCanForceStop(false)
          setImportProgress(null)
          setSelectedFiles([])
          toast.success('Import completed', Helper.bottom_center())
          fetchFiles()
          setActiveTab(1)
          fetchReports()
          fetchEpicRawReports()
        } else if (status === 'killed') {
          clearInterval(pollIntervalRef.current)
          pollIntervalRef.current = null
          setImporting(false)
          setKillPending(false)
          setCanForceStop(false)
          setImportProgress(null)
          setSelectedFiles([])
          toast.info('Import stopped. See the Status tab for rows processed before the stop.', Helper.bottom_center())
          fetchFiles()
          setActiveTab(1)
          fetchReports()
          fetchEpicRawReports()
        } else if (status === 'failed' || status === 'error') {
          clearInterval(pollIntervalRef.current)
          pollIntervalRef.current = null
          setImporting(false)
          setKillPending(false)
          setCanForceStop(false)
          setImportProgress(null)
          toast.error('Import failed. Please try again.', Helper.bottom_center())
        } else if (status === 'never_run') {
          // Import finished but result was lost (e.g. server restart)
          clearInterval(pollIntervalRef.current)
          pollIntervalRef.current = null
          setImporting(false)
          setKillPending(false)
          setCanForceStop(false)
          setImportProgress(null)
          toast.info('Import finished. Check Status tab for details.', Helper.bottom_center())
          fetchFiles()
          setActiveTab(1)
          fetchReports()
          fetchEpicRawReports()
        }
      }).catch(() => {
        clearInterval(pollIntervalRef.current)
        pollIntervalRef.current = null
        if (mountedRef.current) setImporting(false)
      })
    }, 3000)
  }

  const handleDownloadReportByName = (filename: string, type?: 'import' | 'sync') => {
    const downloadFn = type === 'sync'
      ? patientService.downloadUltromicsSyncReportByName(filename)
      : patientService.downloadUltromicsReportByName(filename)
    downloadFn.then((res: any) => {
      if (!mountedRef.current) return
      Helper.handleReportFile(res.data, filename.replace('.csv', ''))
    }).catch((err: any) => {
      if (!mountedRef.current) return
      toast.error(Helper.getErrorText(err), Helper.bottom_center())
    })
  }

  const handleDownloadProcessedFile = (filename: string) => {
    patientService.downloadUltromicsProcessedFile(filename).then((res: any) => {
      if (!mountedRef.current) return
      Helper.handleReportFile(res.data, filename.replace(/\.(csv|xlsx)$/, ''))
    }).catch((err: any) => {
      if (!mountedRef.current) return
      toast.error(Helper.getErrorText(err), Helper.bottom_center())
    })
  }

  const handleDeleteReport = (filename: string, type?: 'import' | 'sync') => {
    setPendingDeleteReport({ name: filename, type: type || 'import' })
  }

  const confirmDeleteReportFile = (confirmed: boolean) => {
    if (confirmed && pendingDeleteReport) {
      const deleteFn = pendingDeleteReport.type === 'sync'
        ? patientService.deleteUltromicsSyncReport(pendingDeleteReport.name)
        : patientService.deleteUltromicsReport(pendingDeleteReport.name)
      deleteFn.then(() => {
        if (!mountedRef.current) return
        toast.success('Report deleted', Helper.bottom_center())
        setPendingDeleteReport(null)
        fetchReports()
      }).catch((err: any) => {
        if (!mountedRef.current) return
        toast.error(Helper.getErrorText(err), Helper.bottom_center())
        setPendingDeleteReport(null)
      })
    } else {
      setPendingDeleteReport(null)
    }
  }

  const handleDownloadMeasurementReport = (filename: string) => {
    patientService.downloadUltromicsMeasurementReport(filename).then((res: any) => {
      if (!mountedRef.current) return
      Helper.handleTaskReportFile(res.data, filename)
    }).catch((err: any) => {
      if (!mountedRef.current) return
      toast.error(Helper.getErrorText(err), Helper.bottom_center())
    })
  }

  const confirmDeleteMeasurementReportFile = (confirmed: boolean) => {
    if (confirmed && pendingDeleteMeasurementReport) {
      patientService.deleteUltromicsMeasurementReport(pendingDeleteMeasurementReport).then(() => {
        if (!mountedRef.current) return
        toast.success('Report deleted', Helper.bottom_center())
        setPendingDeleteMeasurementReport(null)
        fetchMeasurementReports()
      }).catch((err: any) => {
        if (!mountedRef.current) return
        toast.error(Helper.getErrorText(err), Helper.bottom_center())
        setPendingDeleteMeasurementReport(null)
      })
    } else {
      setPendingDeleteMeasurementReport(null)
    }
  }

  const handleDownloadEpicRawReport = (filename: string) => {
    patientService.downloadUltromicsEpicRawReport(filename).then((res: any) => {
      if (!mountedRef.current) return
      Helper.handleTaskReportFile(res.data, filename)
    }).catch((err: any) => {
      if (!mountedRef.current) return
      toast.error(Helper.getErrorText(err), Helper.bottom_center())
    })
  }

  const confirmDeleteEpicRawReportFile = (confirmed: boolean) => {
    if (confirmed && pendingDeleteEpicRawReport) {
      patientService.deleteUltromicsEpicRawReport(pendingDeleteEpicRawReport).then(() => {
        if (!mountedRef.current) return
        toast.success('Report deleted', Helper.bottom_center())
        setPendingDeleteEpicRawReport(null)
        fetchEpicRawReports()
      }).catch((err: any) => {
        if (!mountedRef.current) return
        toast.error(Helper.getErrorText(err), Helper.bottom_center())
        setPendingDeleteEpicRawReport(null)
      })
    } else {
      setPendingDeleteEpicRawReport(null)
    }
  }

  const handleDeleteProcessedFile = (filename: string) => {
    setConfirmDeleteProcessed(filename)
  }

  const confirmDeleteProcessedFile = (confirmed: boolean) => {
    if (confirmed && confirmDeleteProcessed) {
      patientService.deleteUltromicsProcessedFile(confirmDeleteProcessed).then(() => {
        if (!mountedRef.current) return
        toast.success('File deleted', Helper.bottom_center())
        setConfirmDeleteProcessed(null)
        fetchProcessedFiles()
      }).catch((err: any) => {
        if (!mountedRef.current) return
        toast.error(Helper.getErrorText(err), Helper.bottom_center())
        setConfirmDeleteProcessed(null)
      })
    } else {
      setConfirmDeleteProcessed(null)
    }
  }

  const handleUploadFile = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return
    // Reset input so the same file can be re-selected
    if (fileInputRef.current) fileInputRef.current.value = ''
    setUploading(true)
    patientService.uploadUltromicsFile(file).then((res: any) => {
      if (!mountedRef.current) return
      const uploaded = res.data?.uploaded || []
      toast.success(`Uploaded: ${uploaded.join(', ')}`, Helper.bottom_center())
      setUploading(false)
      fetchFiles()
    }).catch((err: any) => {
      if (!mountedRef.current) return
      toast.error(Helper.getErrorText(err), Helper.bottom_center())
      setUploading(false)
    })
  }

  const handleAzureSync = () => {
    setSyncing(true)
    patientService.syncUltromicsFromAzure().then(() => {
      if (!mountedRef.current) return
      toast.success('Azure sync started', Helper.bottom_center())
      startSyncPolling()
    }).catch((err: any) => {
      if (!mountedRef.current) return
      toast.error(Helper.getErrorText(err), Helper.bottom_center())
      setSyncing(false)
    })
  }

  const startSyncPolling = () => {
    if (syncPollRef.current) return
    syncPollCountRef.current = 0
    syncPollRef.current = setInterval(() => {
      syncPollCountRef.current += 1
      patientService.checkUltromicsAzureSyncStatus().then((res: any) => {
        if (!mountedRef.current) {
          clearInterval(syncPollRef.current)
          syncPollRef.current = null
          return
        }
        const status = res.data?.status
        // Skip 'never_run' on early polls to allow backend job pickup time
        if (status === 'never_run' && syncPollCountRef.current < 6) return
        if (status === 'completed' || status === 'never_run' || status === 'failed' || status === 'error') {
          clearInterval(syncPollRef.current)
          syncPollRef.current = null
          setSyncing(false)
          if (status === 'completed') {
            const synced = res.data?.result?.synced?.length || 0
            const errors = res.data?.result?.errors?.length || 0
            if (errors > 0) {
              toast.warning(`Azure sync completed: ${synced} file(s) downloaded, ${errors} error(s)`, Helper.bottom_center())
            } else {
              toast.success(`Azure sync completed: ${synced} file(s) downloaded`, Helper.bottom_center())
            }
            fetchFiles()
            setActiveTab(1)
            fetchReports()
          } else if (status === 'never_run') {
            toast.warning('Azure sync did not start. Please try again.', Helper.bottom_center())
          } else {
            toast.error('Azure sync failed. Please try again.', Helper.bottom_center())
          }
        }
      }).catch(() => {
        clearInterval(syncPollRef.current)
        syncPollRef.current = null
        if (mountedRef.current) setSyncing(false)
      })
    }, 3000)
  }

  const formatFileSize = (bytes: number) => {
    if (bytes < 1024) return `${bytes} B`
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
  }

  const handleClose = (_event: any, reason: any) => {
    if (reason && (reason === 'backdropClick' || reason === 'escapeKeyDown')) return
    onClose()
  }

  return (
    <>
      <Draggable ref={dialogRef} handle='.ultromics-import-dialog'>
        <Dialog
          fullWidth={true}
          maxWidth="md"
          open={openUltromicsImport}
          onClose={handleClose}
          aria-labelledby='Import from Ultromics Output'
          hideBackdrop
          disableScrollLock
          sx={{ position: 'absolute', left: '10%', top: '10%' }}
        >
          <DialogTitle className={classes.addPatientQuestionHeaderSection} classes={{ root: 'ultromics-import-dialog' }}>
            <DialogTitleHeaderComponent title='Import from Ultromics Output' handleDialogClose={onClose} />
          </DialogTitle>
          <Tabs
            value={activeTab}
            onChange={handleTabChange}
            sx={{
              minHeight: '32px',
              borderBottom: '1px solid #e0e0e0',
              '& .MuiTab-root': {
                minHeight: '32px',
                py: 0.5,
                px: 1.5,
                fontSize: '0.75rem',
                textTransform: 'none',
              },
              '& .MuiTabs-indicator': {
                height: '2px',
              },
            }}
          >
            <Tab label="Import" />
            <Tab label="Status" />
            <Tab label="History" />
            <Tab label="Measurement Reports" />
            <Tab label="EPIC Raw JSON" />
          </Tabs>
          <Grid item>{(loading || importing || loadingReports || loadingProcessed || loadingMeasurementReports || loadingEpicRawReports || syncing) && <LinearProgress />}</Grid>
          <DialogContent className={classes.addPatientDialogContent} sx={{ overflowY: 'auto', padding: '0 !important' }}>
            {activeTab === 0 && (
              <>
                {files.length === 0 && !loading ? (
                  <Typography variant="body2" sx={{ padding: '1rem', textAlign: 'center', color: 'grey' }}>
                    No CSV files found in the Ultromics import directory.
                  </Typography>
                ) : (
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell padding="checkbox">
                          <Checkbox
                            size="small"
                            disabled={importing || files.length === 0}
                            checked={files.length > 0 && selectedFiles.length === files.length}
                            indeterminate={selectedFiles.length > 0 && selectedFiles.length < files.length}
                            onChange={(e) => handleSelectAll(e.target.checked)}
                          />
                        </TableCell>
                        <TableCell><strong>File Name</strong></TableCell>
                        <TableCell><strong>Size</strong></TableCell>
                        <TableCell><strong>Modified</strong></TableCell>
                        <TableCell align="center"><strong>Action</strong></TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {files.map((file) => (
                        <TableRow key={file.name} hover>
                          <TableCell padding="checkbox">
                            <Checkbox
                              size="small"
                              disabled={importing}
                              checked={selectedFiles.includes(file.name)}
                              onChange={(e) => handleSelectFile(file.name, e.target.checked)}
                            />
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2">{file.name}</Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2">{formatFileSize(file.size)}</Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2">{moment.unix(file.modified).format('MM/DD/YYYY hh:mm A')}</Typography>
                          </TableCell>
                          <TableCell align="center">
                            <Tooltip title="Delete file">
                              <IconButton size="small" disabled={importing} onClick={() => handleDeleteFile(file.name)}>
                                <DeleteOutlinedIcon sx={{ fontSize: '0.9rem' }} />
                              </IconButton>
                            </Tooltip>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </>
            )}
            {activeTab === 1 && (
              <>
                {reports.length === 0 && !loadingReports ? (
                  <Typography variant="body2" sx={{ padding: '1rem', textAlign: 'center', color: 'grey' }}>
                    No reports found.
                  </Typography>
                ) : (
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell><strong>Report Name</strong></TableCell>
                        <TableCell><strong>Type</strong></TableCell>
                        <TableCell><strong>Size</strong></TableCell>
                        <TableCell><strong>Created</strong></TableCell>
                        <TableCell align="center"><strong>Action</strong></TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {reports.map((report) => (
                        <TableRow key={`${report.type}-${report.name}`} hover>
                          <TableCell>
                            <Typography variant="body2">{report.name}</Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" sx={{ textTransform: 'capitalize' }}>{report.type === 'sync' ? 'Azure Sync' : 'Import'}</Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2">{formatFileSize(report.size)}</Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2">{moment.unix(report.created).format('MM/DD/YYYY hh:mm A')}</Typography>
                          </TableCell>
                          <TableCell align="center">
                            <Tooltip title="Download report">
                              <IconButton size="small" onClick={() => handleDownloadReportByName(report.name, report.type)}>
                                <GetAppIcon sx={{ fontSize: '0.9rem' }} />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Delete report">
                              <IconButton size="small" onClick={() => handleDeleteReport(report.name, report.type)}>
                                <DeleteOutlinedIcon sx={{ fontSize: '0.9rem' }} />
                              </IconButton>
                            </Tooltip>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </>
            )}
            {activeTab === 2 && (
              <>
                {processedFiles.length === 0 && !loadingProcessed ? (
                  <Typography variant="body2" sx={{ padding: '1rem', textAlign: 'center', color: 'grey' }}>
                    No files in history.
                  </Typography>
                ) : (
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell><strong>File Name</strong></TableCell>
                        <TableCell><strong>Size</strong></TableCell>
                        <TableCell><strong>Modified</strong></TableCell>
                        <TableCell align="center"><strong>Action</strong></TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {processedFiles.map((file) => (
                        <TableRow key={file.name} hover>
                          <TableCell>
                            <Typography variant="body2">{file.name}</Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2">{formatFileSize(file.size)}</Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2">{moment.unix(file.modified).format('MM/DD/YYYY hh:mm A')}</Typography>
                          </TableCell>
                          <TableCell align="center">
                            <Tooltip title="Download file">
                              <IconButton size="small" onClick={() => handleDownloadProcessedFile(file.name)}>
                                <GetAppIcon sx={{ fontSize: '0.9rem' }} />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Delete file">
                              <IconButton size="small" onClick={() => handleDeleteProcessedFile(file.name)}>
                                <DeleteOutlinedIcon sx={{ fontSize: '0.9rem' }} />
                              </IconButton>
                            </Tooltip>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </>
            )}
            {activeTab === 3 && (
              <>
                {measurementReports.length === 0 && !loadingMeasurementReports ? (
                  <Typography variant="body2" sx={{ padding: '1rem', textAlign: 'center', color: 'grey' }}>
                    No measurement parsing reports found. One workbook is generated per imported
                    file, with a worksheet per patient MRN.
                  </Typography>
                ) : (
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell><strong>Report Name</strong></TableCell>
                        <TableCell><strong>Size</strong></TableCell>
                        <TableCell><strong>Created</strong></TableCell>
                        <TableCell align="center"><strong>Action</strong></TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {measurementReports.map((report) => (
                        <TableRow key={report.name} hover>
                          <TableCell>
                            <Typography variant="body2">{report.name}</Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2">{formatFileSize(report.size)}</Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2">{moment.unix(report.created).format('MM/DD/YYYY hh:mm A')}</Typography>
                          </TableCell>
                          <TableCell align="center">
                            <Tooltip title="Download report">
                              <IconButton size="small" onClick={() => handleDownloadMeasurementReport(report.name)}>
                                <GetAppIcon sx={{ fontSize: '0.9rem' }} />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Delete report">
                              <IconButton size="small" onClick={() => setPendingDeleteMeasurementReport(report.name)}>
                                <DeleteOutlinedIcon sx={{ fontSize: '0.9rem' }} />
                              </IconButton>
                            </Tooltip>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </>
            )}
            {activeTab === 4 && (
              <>
                {epicRawReports.length === 0 && !loadingEpicRawReports ? (
                  <Typography variant="body2" sx={{ padding: '1rem', textAlign: 'center', color: 'grey' }}>
                    No EPIC raw JSON documents found. One document is generated per imported MRN,
                    containing every raw EPIC FHIR response fetched during that patient's import.
                  </Typography>
                ) : (
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell><strong>MRN</strong></TableCell>
                        <TableCell><strong>File Name</strong></TableCell>
                        <TableCell><strong>Size</strong></TableCell>
                        <TableCell><strong>Created</strong></TableCell>
                        <TableCell align="center"><strong>Action</strong></TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {epicRawReports.map((report) => (
                        <TableRow key={report.name} hover>
                          <TableCell>
                            <Typography variant="body2">{report.mrn || '—'}</Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2">{report.name}</Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2">{formatFileSize(report.size)}</Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2">{moment.unix(report.created).format('MM/DD/YYYY hh:mm A')}</Typography>
                          </TableCell>
                          <TableCell align="center">
                            <Tooltip title={`Download EPIC Raw JSON — ${report.mrn || report.name}`}>
                              <IconButton size="small" onClick={() => handleDownloadEpicRawReport(report.name)}>
                                <GetAppIcon sx={{ fontSize: '0.9rem' }} />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Delete document">
                              <IconButton size="small" onClick={() => setPendingDeleteEpicRawReport(report.name)}>
                                <DeleteOutlinedIcon sx={{ fontSize: '0.9rem' }} />
                              </IconButton>
                            </Tooltip>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </>
            )}
          </DialogContent>
          <DialogActions>
            <input
              accept=".csv,.xlsx"
              style={{ display: 'none' }}
              ref={fileInputRef}
              type="file"
              onChange={handleUploadFile}
            />
            <Grid container spacing={1} className={classes.containerPadding} justifyContent="flex-end" alignItems="center">
              {(importing || uploading || syncing) && (
                <Grid item>
                  <Typography variant="body2" color="grey"><i>{uploading ? 'Uploading...' : syncing ? 'Syncing from Azure...' : 'Import in progress...'}</i></Typography>
                </Grid>
              )}
              {importing && importProgress && (
                <Grid item>
                  <Typography variant="caption" sx={{ color: 'var(--color-text-secondary)' }}>
                    Importing {importProgress.current_file} — file {importProgress.file_index}/{importProgress.total_files},
                    {' '}{importProgress.rows_processed} row(s) processed
                  </Typography>
                </Grid>
              )}
              {importing && (
                <Grid item>
                  <Button
                    color="error"
                    variant="outlined"
                    size="small"
                    onClick={handleStopImport}
                    disabled={killPending && !canForceStop}
                    sx={{ height: '32px', textTransform: 'none' }}
                  >
                    {killPending ? (canForceStop ? 'Force Stop' : 'Stopping…') : 'Stop Import'}
                  </Button>
                </Grid>
              )}
              {activeTab === 0 && (
                <>
                  <Grid item>
                    <Tooltip title={
                      !azureSyncEnabled
                        ? 'Azure Blob Storage is not configured. Please configure the connection in the backend settings to enable syncing.'
                        : syncing
                        ? 'Azure sync is in progress...'
                        : importing || loading || uploading
                        ? 'Another operation is in progress.'
                        : ''
                    }>
                      <span>
                        <Button
                          variant="outlined"
                          size="small"
                          startIcon={<SyncIcon />}
                          disabled={!azureSyncEnabled || importing || loading || uploading || syncing}
                          onClick={handleAzureSync}
                          sx={{
                            height: '32px',
                            textTransform: 'none',
                            '&.Mui-disabled': { opacity: 0.4 },
                          }}
                        >
                          {syncing ? 'Syncing...' : 'Sync from Azure'}
                        </Button>
                      </span>
                    </Tooltip>
                  </Grid>
                  <Grid item>
                    <Button
                      variant="outlined"
                      size="small"
                      startIcon={<CloudUploadOutlinedIcon />}
                      disabled={importing || loading || uploading || syncing}
                      onClick={() => fileInputRef.current?.click()}
                      sx={{ height: '32px', textTransform: 'none' }}
                    >
                      Upload Ultromics File
                    </Button>
                  </Grid>
                  <Grid item>
                    <Button
                      variant="outlined"
                      size="small"
                      disabled={selectedFiles.length === 0 || importing || loading || uploading || syncing}
                      onClick={handleStartImport}
                      sx={{
                        height: '32px',
                        textTransform: 'none',
                        '&.Mui-disabled': { opacity: 0.4 },
                      }}
                    >
                      Import Selected ({selectedFiles.length})
                    </Button>
                  </Grid>
                </>
              )}
              <Grid item>
                <Button variant="outlined" size="small" onClick={onClose} disabled={importing || uploading || syncing} sx={{ height: '32px', textTransform: 'none' }}>
                  Close
                </Button>
              </Grid>
            </Grid>
          </DialogActions>
        </Dialog>
      </Draggable>
      {confirmDelete && (
        <ConfirmationDialogComponent
          open={!!confirmDelete}
          content={`Are you sure you want to delete "${confirmDelete}"?`}
          handleConfirmClose={() => confirmDeleteFile(false)}
          confirmStatus={() => confirmDeleteFile(true)}
        />
      )}
      {pendingDeleteReport && (
        <ConfirmationDialogComponent
          open={!!pendingDeleteReport}
          content={`Are you sure you want to delete report "${pendingDeleteReport.name}"?`}
          handleConfirmClose={() => confirmDeleteReportFile(false)}
          confirmStatus={() => confirmDeleteReportFile(true)}
        />
      )}
      {confirmDeleteProcessed && (
        <ConfirmationDialogComponent
          open={!!confirmDeleteProcessed}
          content={`Are you sure you want to delete "${confirmDeleteProcessed}" from history?`}
          handleConfirmClose={() => confirmDeleteProcessedFile(false)}
          confirmStatus={() => confirmDeleteProcessedFile(true)}
        />
      )}
      {pendingDeleteEpicRawReport && (
        <ConfirmationDialogComponent
          open={!!pendingDeleteEpicRawReport}
          content={`Are you sure you want to delete document "${pendingDeleteEpicRawReport}"?`}
          handleConfirmClose={() => confirmDeleteEpicRawReportFile(false)}
          confirmStatus={() => confirmDeleteEpicRawReportFile(true)}
        />
      )}
      {pendingDeleteMeasurementReport && (
        <ConfirmationDialogComponent
          open={!!pendingDeleteMeasurementReport}
          content={`Are you sure you want to delete report "${pendingDeleteMeasurementReport}"?`}
          handleConfirmClose={() => confirmDeleteMeasurementReportFile(false)}
          confirmStatus={() => confirmDeleteMeasurementReportFile(true)}
        />
      )}
      {confirmStop && (
        <ConfirmationDialogComponent
          open={confirmStop}
          content={canForceStop
            ? 'Force-stop the import? Use only if it appears frozen.'
            : 'Stop the running import? Rows already imported will remain; the current file will not be archived and can be re-run.'}
          handleConfirmClose={() => confirmStopImport(false)}
          confirmStatus={() => confirmStopImport(true)}
        />
      )}
    </>
  )
}

export default withStyles(stylesheet)(UltromicsImportDialogComponent)
